// eslint-disable-next-line no-restricted-imports
import type { FieldError } from 'react-hook-form';
// eslint-disable-next-line no-restricted-imports
import { useController, useFormContext, useFormState } from 'react-hook-form';
import { type FocusEvent, useCallback, useEffect, useRef } from 'react';

import type { ErrorDetails, TypedFieldName, TypedFieldNameValue } from './type';
import { getCheckedValue, getInnerError } from './useFieldService';
import { get } from './ErrorsService';

type TypedChangeEvent = {
    target: {
        value: unknown;
    };
};

type SetError = (errorDetails: ErrorDetails) => void;

export type FieldInputProps<FieldValue> = {
    /**
     * Don't use it to read value across the form - use useWatchFieldValue instead if needed
     */
    value: FieldValue;
    name: string;
    checked?: boolean;
    onChange: (e: TypedChangeEvent) => void;
    onBlur: (e?: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};
type FieldMetaProps<FieldValue> = {
    /**
     * Don't use it to read value across the form - use useWatchFieldValue instead if needed.
     * Same as FieldInputProps value -
     * @deprecated - present for backwards compatibility reasons
     */
    value: FieldValue;
    error?: string;
    innerError?: string;
    touched: boolean;
    dirty: boolean;
    /**
     * Custom error type defined when using setError from useFormContext,
     * useful when you'd like to distinguish between different error types.
     * */
    errorType?: FieldError['type'];
};
type FieldHelperProps<FieldValue> = {
    setValue: (value?: FieldValue | null | undefined) => void;
    setTouched: () => void;
    setError: SetError;
};
type ReturnType<FieldValue> = [
    FieldInputProps<FieldValue>,
    FieldMetaProps<FieldValue>,
    FieldHelperProps<FieldValue>,
];
/**
 * Options for configuring the field behavior.
 */
type FieldOptions = {
    /** The type of the field. */
    type?: string;
    /** Whether to clear the field when the component unmounts. */
    shouldClearOnUnmount?: boolean;
};

/**
 * A custom hook for managing form fields.
 * @template FieldValue The type of the field value.
 * @template FormValue The type of the overall form values.
 * @param {TypedFieldName<FormValue>} name The name of the field.
 * @param {FieldOptions} [options] Optional configuration for the field.
 * @returns {ReturnType<FieldValue>} An array containing input props, meta props, and helper functions for the field.
 *
 * @example
 * ```tsx
 * import { useField } from '@luna/form2';
 *
 * interface FormValues {
 *   email: string;
 * }
 *
 * const EmailField = () => {
 *   const [field, meta, helpers] = useField<string, FormValues>('email', { type: 'text' });
 *
 *   return (
 *     <div>
 *       <input
 *         {...field}
 *         type="email"
 *         placeholder="Enter your email"
 *       />
 *       {meta.touched && meta.error && (
 *         <div className="error">{meta.error}</div>
 *       )}
 *       <button onClick={() => helpers.setValue('')}>Clear</button>
 *     </div>
 *   );
 * };
 *```
 */
export const useField = <FieldValue = string, FormValue = void>(
    name: TypedFieldName<FormValue>,
    options?: FieldOptions,
): ReturnType<FieldValue> => {
    'use no memo';

    const {
        field: { onChange: fieldOnChange, onBlur: fieldOnBlur, name: fieldName, value },
        fieldState,
    } = useController({
        name: name as unknown as string,
        shouldUnregister: options?.shouldClearOnUnmount,
    });

    const { errors } = useFormState();
    const { clearErrors, setError } = useFormContext();

    const rootErrorName = `root.${name}`;
    const rootError = get(errors, rootErrorName);

    const { isTouched, isDirty, error } = fieldState;

    const clearRootErrorIfNeeded = useCallback(() => {
        if (rootError) {
            clearErrors(rootErrorName);
        }
    }, [clearErrors, rootError, rootErrorName]);

    const onChange = useCallback(
        (e: TypedChangeEvent) => {
            clearRootErrorIfNeeded();
            return fieldOnChange(e.target.value);
        },
        [fieldOnChange, clearRootErrorIfNeeded],
    );
    const isAfterInitialRenderRef = useRef(false);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            isAfterInitialRenderRef.current = true;
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            isAfterInitialRenderRef.current = false;
        };
    }, []);

    const setValue = useCallback(
        (v: FieldValue | null | undefined) => {
            clearRootErrorIfNeeded();

            if (!isAfterInitialRenderRef.current) {
                // https://github.com/react-hook-form/react-hook-form/issues/11056
                // Prevents useWatchFieldValue not working when setting values in
                // initial useEffect()
                setTimeout(() => {
                    fieldOnChange(v);
                }, 0);
                return;
            }

            fieldOnChange(v);
        },
        [fieldOnChange, clearRootErrorIfNeeded],
    );

    const onBlur = useCallback(() => {
        // Prevents validation messages from flashing during onChange validation.
        return setTimeout(() => {
            fieldOnBlur();
            clearRootErrorIfNeeded();
        }, 0);
    }, [fieldOnBlur, clearRootErrorIfNeeded]);

    const handleSetError: SetError = useCallback(
        ({ errorType, message, shouldFocus }) =>
            setError(name, { type: errorType, message }, { shouldFocus }),
        [name, setError],
    );

    return [
        {
            value,
            onChange,
            onBlur,
            name: fieldName,
            checked: getCheckedValue(value, options),
        },
        {
            value,
            touched: isTouched,
            dirty: isDirty,
            error: error?.message || rootError?.message,
            innerError: getInnerError(fieldName, errors),
            errorType: error?.type,
        },
        {
            setValue,
            setTouched: onBlur,
            setError: handleSetError,
        },
    ];
};

export const createTypedUseField = <FormValues>() => {
    return <Name extends TypedFieldName<FormValues>>(
        name: Name,
    ): ReturnType<TypedFieldNameValue<FormValues, Name>> =>
        useField<TypedFieldNameValue<FormValues, Name>, FormValues>(name);
};
